import React, { createContext, PropsWithChildren, useContext, useMemo, useReducer, useEffect } from 'react';

export type TReportFormContext = {
    form: {
        title: { id: typeof initialState.title.id, value: any };
        description: { id: typeof initialState.description.id, value: any };
        dropdown: { id: typeof initialState.dropdown.id, value: any };
        severity: { id: typeof initialState.severity.id, value: any };
        image: { id: typeof initialState.image.id, value: { base64: any, file: any } };
    };
    updateForm: (action: { field?: keyof typeof initialState, value?: any, reset?: boolean }) => void;
}

// 1. Create Context
const ReportFormContext = createContext<TReportFormContext | null>(null);

// 2. Create custom hook
export const useReportFormContext = () => {
    const context = useContext(ReportFormContext);

    if (!context) {
        throw new Error('useAuthContext must be used within a ReportFormProvider');
    }
    return context;
}

type TState = {
    title: {
        id: string,
        value: string,
    },
    description: {
        id: string,
        value: string,
    },
    dropdown: {
        id: string,
        value: { name: string, id: string },
    },
    severity: {
        id: string,
        value: string,
    },
    image: {
        id: string,
        value: { base64: string, file: string },
    },
}

type TAction = {
    field?: keyof typeof initialState;
    value?: any;
    reset?: boolean;
}

const reducer = (state: TState, action: TAction) => {
    const updatedState = { ...state };
    if (action.field) {
        updatedState[action.field] = { ...updatedState[action.field], value: action.value };
    }

    return action.reset ? initialState : updatedState;
}

const initialState = {
    title: {
        id: 'title',
        value: '',
    },
    description: {
        id: 'description',
        value: '',
    },
    dropdown: {
        id: 'dropdown',
        value: { name: '', id: '' },
    },
    severity: {
        id: 'severity',
        value: '',
    },
    image: {
        id: 'image',
        value: { base64: '', file: '' },
    },
} as const;

// 3. Create Provider
export const ReportFormProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    useEffect(() => {
        return () => {
            dispatch({ reset: true });
        };
    }, []);

    const value = useMemo(() => {
        return { form: state, updateForm: dispatch }
    }, [state])

    return <ReportFormContext.Provider value={value as any}>{children}</ReportFormContext.Provider>;
}